// The questions the app asks: a password, the font substitute (AC-FONT-2),
// what to do when the new text does not fit (AC-EDIT-5), and deleted text
// still in the document before export (AC-DEL-3), the export's file name (AC-MODE-3). Also the view-only notice.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MIN_SHRINK, type FitInfo, type PlanInfo, type ResidualInfo } from '../../engine/worker/protocol.ts';
import type { LimitKind } from '../../engine/layout/room.ts';
import type { MatchGrade } from '../../engine/font/bundled.ts';
import type { PasswordPrompt, Session } from '../model.ts';
import type { PageKind } from '../ocr.ts';
import { cleanName } from '../export-name.ts';

const GRADE_TEXT: Record<MatchGrade, string> = {
  identical: 'It looks exactly like the original (identical glyph shapes); only the missing characters use it.',
  'same-widths': 'It takes the same room as the original, but the glyph shapes differ slightly.',
  similar: 'It is similar to the original; widths and shapes differ a little.',
  unknown: 'It could not be compared with the original; check the result.',
  different: 'Warning: it looks clearly different from the original (other widths and shapes).',
};

const GRADE_SHORT: Record<MatchGrade, string> = { identical: 'identical shapes', 'same-widths': 'same widths', similar: 'similar', unknown: 'not compared', different: 'different' };

const LIMIT_NAMES: Record<LimitKind, string> = { rule: 'a table line', box: 'the edge of a shaded box', text: 'other text', image: 'an image', column: 'the edge of its text column', clip: 'the edge of the area it is clipped to (such as its table cell)', page: 'the edge of the page' };

export function PasswordForm(props: { prompt: PasswordPrompt; busy: boolean; onSubmit: (password: string) => void; onCancel: () => void }) {
  const { prompt } = props;
  return (
    <form
      data-testid="password-prompt"
      className="panel panel-row modal"
      role="dialog"
      aria-modal="true"
      aria-label="Password"
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit(String(new FormData(e.currentTarget).get('password') ?? ''));
      }}
    >
      <span>{prompt.owner ? `Owner password for ${prompt.name}:` : `${prompt.name} is password-protected. Password:`}</span>
      <input data-testid="password-input" name="password" type="password" autoFocus autoComplete="off" />
      <button data-testid="password-submit" type="submit" disabled={props.busy}>
        Open
      </button>
      <button type="button" onClick={props.onCancel}>
        Cancel
      </button>
      {prompt.wrong && (
        <span data-testid="password-wrong" role="alert" className="text-error">
          Incorrect password.
        </span>
      )}
    </form>
  );
}

/** The exported copy's file name, asked first (AC-MODE-3): suggested, cleaned as typed, never the original's. */
export function ExportNameForm(props: { original: string; suggested: string; onExport: (name: string) => void; onCancel: () => void }) {
  const [typed, setTyped] = useState(props.suggested);
  const name = cleanName(typed);
  const same = !!name && name.toLowerCase() === props.original.toLowerCase();
  return (
    <form
      data-testid="export-name-dialog"
      className="panel modal"
      role="dialog"
      aria-modal="true"
      aria-label="Export a copy"
      onSubmit={(e) => {
        e.preventDefault();
        if (name && !same) props.onExport(name);
      }}
    >
      <label className="export-name">
        <span>Export a copy as</span>
        <input
          data-testid="export-name"
          type="text"
          value={typed}
          autoFocus
          spellCheck={false}
          onChange={(e) => setTyped(e.target.value)}
          // The name without ".pdf" selected, ready to type over.
          onFocus={(e) => e.currentTarget.setSelectionRange(0, typed.replace(/\.pdf$/i, '').length)}
        />
      </label>
      <p data-testid="export-name-note" className="small" role={same ? 'alert' : undefined}>
        {same ? 'That is the name of the original: choose another, so the original stays as it is.' : name && name !== typed.trim() ? `Saved as ${name}` : 'The original file is not changed.'}
      </p>
      <div className="actions">
        <button type="button" data-testid="export-name-cancel" onClick={props.onCancel}>
          Cancel
        </button>
        <button type="submit" data-testid="export-name-ok" className="btn-primary" disabled={!name || same}>
          Export
        </button>
      </div>
    </form>
  );
}

export function ViewOnlyNotice(props: { session: Session; onOwnerPassword: () => void }) {
  return (
    <p data-testid="view-only" className="notice notice-warn" role="status">
      View only: {props.session.viewOnly}. Editing and export are turned off.{' '}
      {props.session.restricted && (
        <button data-testid="owner-password" onClick={props.onOwnerPassword}>
          Open with the owner password
        </button>
      )}
    </p>
  );
}

/**
 * A font name as people know it: without the subset tag ("ABCDEF+") and without a second random tag some writers
 * add before the real name (Firefox: "RUMLVE" and a non-ASCII character before "Helvetica-Bold"). Display only.
 */
export function fontDisplayName(name: string): string {
  return name.replace(/^[A-Z]{6}\+/, '').replace(/^[A-Z]{6}[^\x00-\x7f]+(?=[A-Za-z])/, '');
}

export function SubstituteChoice(props: { plan: Extract<PlanInfo, { kind: 'substitute' }>; onYes: () => void; onNo: () => void; onPick: (face: string) => void }) {
  const { plan } = props;
  const warn = plan.grade === 'different';
  return (
    <div data-testid="confirm-substitute" data-grade={plan.grade} className={`panel ${warn ? 'panel-danger' : 'panel-ask'}`} role="dialog" aria-label="Font substitution">
      <p>
        The original font ({fontDisplayName(plan.from)}) has no glyph for {plan.missing.map((c) => `"${c}"`).join(', ')}. The edited text would use <b>{plan.to}</b> instead.
      </p>
      <p data-testid="confirm-grade">
        {GRADE_TEXT[plan.grade]}
        {plan.compared > 0 && ` (widths match on ${plan.compared - plan.widthMismatches} of ${plan.compared} characters)`}
      </p>
      {plan.choices && (
        <fieldset data-testid="substitute-choices" className="choices">
          <legend>Fonts to choose from:</legend>
          {plan.choices.map((c, k) => (
            <label key={c.face}>
              <input type="radio" name="substitute" value={c.face} checked={c.face === plan.face} onChange={() => props.onPick(c.face)} />
              <span>
                {c.label}
                {k === 0 && ' (suggested)'}: {GRADE_SHORT[c.grade]}
                {c.compared > 0 && `, ${c.compared - c.widthMismatches} of ${c.compared} widths match`}
              </span>
            </label>
          ))}
        </fieldset>
      )}
      <div className="actions">
        <button data-testid="confirm-yes" onClick={props.onYes}>
          {warn ? `Use ${plan.to} anyway` : `Use ${plan.to}`}
        </button>
        <button data-testid="confirm-no" onClick={props.onNo}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function OverflowChoice(props: { fit: FitInfo; onShrink: () => void; onKeep: () => void; onCancel: () => void }) {
  const { fit } = props;
  const offPage = fit.limit === 'page';
  return (
    <div data-testid="overflow" className="panel panel-ask" role="dialog" aria-label="The new text does not fit">
      <p>
        The new text is {fit.by.toFixed(1)} pt too wide: it would run into {LIMIT_NAMES[fit.limit]} on the {fit.side}.
        {fit.scale < MIN_SHRINK && ` Narrowing it to fit would take ${Math.floor(fit.scale * 100)}%, below the ${MIN_SHRINK * 100}% limit.`}
        {offPage && ' Text past the edge of the page cannot be seen, so it cannot overflow there.'}
      </p>
      <div className="actions">
        {fit.scale >= MIN_SHRINK && (
          <button data-testid="overflow-shrink" onClick={props.onShrink}>
            Narrow it to {Math.floor(fit.scale * 1000) / 10}%
          </button>
        )}
        {!offPage && (
          <button data-testid="overflow-keep" onClick={props.onKeep}>
            Let it overflow
          </button>
        )}
        <button data-testid="overflow-cancel" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function ResidualsDialog(props: { residuals: ResidualInfo[]; onExport: () => void; onCancel: () => void }) {
  return (
    <div data-testid="residuals" className="panel modal" role="dialog" aria-modal="true" aria-label="Deleted text still in the document">
      <p>Text you deleted or replaced still occurs elsewhere in the document:</p>
      <ul data-testid="residuals-list">
        {props.residuals.map((r, k) => (
          <li key={k}>{r.text ? `“${r.text}” in ${r.where}` : r.where.charAt(0).toUpperCase() + r.where.slice(1)}</li>
        ))}
      </ul>
      <p className="small">Text drawn as shapes, or inside images, cannot be found by this check.</p>
      <div className="actions">
        <button data-testid="residuals-cancel" onClick={props.onCancel}>
          Cancel
        </button>
        <button data-testid="residuals-export" onClick={props.onExport}>
          Export anyway
        </button>
      </div>
    </div>
  );
}

/** The keyboard stays inside a dialog: Tab wraps around its buttons and fields; Escape cancels. */
function trapKeys(e: React.KeyboardEvent<HTMLElement>, onCancel: () => void): void {
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    onCancel();
    return;
  }
  if (e.key !== 'Tab') return;
  const items = [...e.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [href]')];
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/** A dialog over the whole window (AC-UX-6): the rest cannot be used until it is answered; focus starts inside. */
export function Modal(props: { onCancel: () => void; children: React.ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current!;
    // A field or button that asked for focus keeps it; otherwise the first button gets it.
    if (!el.contains(document.activeElement)) el.querySelector<HTMLElement>('input, button')?.focus();
  }, []);
  return (
    <div className="modal-backdrop" ref={box} onKeyDown={(e) => trapKeys(e, props.onCancel)}>
      {props.children}
    </div>
  );
}

/**
 * A question shown next to what it is about (AC-UX-5): below the element `anchor` finds (above when there is no
 * room below), kept inside the window, following it while the page scrolls; focus on its first button.
 */
export function Popover(props: { anchor: () => Element | null; onCancel: () => void; children: React.ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number }>();
  useLayoutEffect(() => {
    const place = () => {
      const a = props.anchor()?.getBoundingClientRect();
      const el = box.current;
      if (!el) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const margin = 12;
      const gap = 8;
      if (!a) {
        setAt({ left: Math.max(margin, (window.innerWidth - w) / 2), top: margin + 72 });
        return;
      }
      const below = a.bottom + gap;
      const top = below + h <= window.innerHeight - margin || a.top - gap - h < margin ? below : a.top - gap - h;
      setAt({
        left: Math.min(Math.max(margin, a.left), window.innerWidth - w - margin),
        top: Math.min(Math.max(margin, top), window.innerHeight - h - margin),
      });
    };
    place();
    window.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place);
      window.removeEventListener('resize', place);
    };
  }, []);
  // Focus once it is placed (hidden until then, and a hidden button cannot take focus).
  const focused = useRef(false);
  useEffect(() => {
    if (!at || focused.current) return;
    focused.current = true;
    box.current?.querySelector<HTMLElement>('button')?.focus();
  }, [at]);
  return (
    <div ref={box} data-testid="popover" className="popover" style={at ? { left: at.left, top: at.top } : { visibility: 'hidden', left: 0, top: 0 }} onKeyDown={(e) => trapKeys(e, props.onCancel)}>
      {props.children}
    </div>
  );
}

/**
 * Enhance (D-50, AC-OCR-5): recognise the text of the page in view or of every scanned page. A page with its own
 * invisible text (from the scanner) can keep it or be recognised again.
 */
export function EnhanceMenu(props: { page: number; kind?: PageKind; onPage: (again: boolean) => void; onAll: () => void; onClose: () => void }) {
  return (
    <div data-testid="enhance-menu" className="panel panel-ask" role="dialog" aria-label="Enhance">
      <p>Enhance recognises the text of scanned pages, so each word can be selected. It runs in this browser; nothing is sent anywhere.</p>
      {props.kind === 'layer' ? (
        <>
          <p>Page {props.page + 1} already has its own invisible text, probably from the scanner.</p>
          <div className="actions">
            <button data-testid="enhance-keep" onClick={props.onClose}>
              Use the existing text
            </button>
            <button data-testid="enhance-again" onClick={() => props.onPage(true)}>
              Recognise again
            </button>
          </div>
        </>
      ) : (
        props.kind === 'none' && <p>Page {props.page + 1} is not a scan.</p>
      )}
      <div className="actions">
        {props.kind !== 'layer' && (
          <button data-testid="enhance-page" disabled={props.kind === 'none'} onClick={() => props.onPage(false)}>
            Page {props.page + 1}
          </button>
        )}
        <button data-testid="enhance-all" onClick={props.onAll}>
          All scanned pages
        </button>
        <button data-testid="enhance-close" onClick={props.onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
